import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

const sevColor = {
  critical: "#ff3333",
  high: "#ffd700",
  medium: "#a3a3a3",
};

export default function Compliance() {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    api.get("/compliance").then((r) => {
      setItems(r.data.items);
      // Seed checkboxes from backend `default_done` — user's manual overrides
      // (saved under `solspur_compliance`) take precedence.
      const overrides = JSON.parse(localStorage.getItem("solspur_compliance") || "{}");
      const legacy = JSON.parse(localStorage.getItem("emg_compliance") || "{}");
      const seeded = {};
      for (const item of r.data.items) {
        if (item.id in overrides) seeded[item.id] = overrides[item.id];
        else if (item.id in legacy) seeded[item.id] = legacy[item.id];
        else if (item.default_done) seeded[item.id] = true;
      }
      setChecked(seeded);
    }).catch(() => {});
  }, []);

  const toggle = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem("solspur_compliance", JSON.stringify(next));
  };

  const total = items.length;
  const done = items.filter((i) => checked[i.id]).length;

  return (
    <section id="compliance" className="border-t border-[#3a3a3a]" data-testid="section-compliance">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 lg:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
              07 // Legal & Compliance
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter">
              Keep it legal.
              <br />
              Keep it cheap.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 self-end">
            <div className="border border-[#3a3a3a] bg-[#121212] p-5">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
                Compliance Score
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-display text-4xl font-black" data-testid="compliance-score">
                  {done}/{total}
                </div>
                <ShieldCheck size={18} className="text-[#cfff04]" />
              </div>
              <div className="h-1 bg-[#1a1a1a] mt-3">
                <div
                  className="h-1 bg-[#cfff04] transition-all"
                  style={{ width: `${total ? (done / total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border border-[#3a3a3a] bg-[#121212]" data-testid="compliance-list">
          {items.map((it) => {
            const isChecked = !!checked[it.id];
            return (
              <button
                key={it.id}
                onClick={() => toggle(it.id)}
                data-testid={`compliance-${it.id}`}
                className="w-full text-left grid grid-cols-12 gap-3 px-6 py-5 border-b border-[#161616] last:border-b-0 hover:bg-[#1a1a1a] transition-colors items-start"
              >
                <div className="col-span-1 pt-0.5">
                  <div
                    className={`w-5 h-5 border-2 flex items-center justify-center ${
                      isChecked ? "bg-[#cfff04] border-[#cfff04]" : "border-[#444]"
                    }`}
                  >
                    {isChecked && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#050505" strokeWidth="3" />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-8 text-sm">
                  <div className="text-[#efefef]">{it.label}</div>
                  {it.note && (
                    <div className="mt-1 text-sm text-[#c4c4c4] leading-relaxed">
                      // {it.note}
                    </div>
                  )}
                </div>
                <div className="col-span-6 sm:col-span-2 font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2]">
                  {it.region}
                </div>
                <div
                  className="col-span-6 sm:col-span-1 font-mono text-[11px] uppercase tracking-widest text-right"
                  style={{ color: sevColor[it.severity] }}
                >
                  {it.severity}
                </div>
              </button>
            );
          })}
        </div>

        <p className="mt-6 text-sm text-[#c4c4c4]">
          // Not legal advice. Consult a licensed attorney in your jurisdiction before launching a fundraising token.
        </p>
      </div>
    </section>
  );
}
